/**
 * <s-next-delivery>
 * Shows the earliest delivery date and time slot a customer can still order,
 * using the same rule format as <s-delivery-slots> on the product page:
 *   days | label | cutoff | time
 * Closed dates (data-closed-dates, comma separated) close a whole day, or
 * only the listed slots when written as "YYYY-MM-DD | slot".
 */
const SG_OFFSET_MS = 8 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_NAMES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

class SNextDelivery extends HTMLElement {
  #timer;
  #rules = [];

  connectedCallback() {
    this.#rules = this.#parseRules(this.dataset.slotRules || '');
    this.#render();
    this.#timer = setInterval(() => this.#render(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #parseRules(text) {
    return text
      .split(/\r?\n/)
      .map((line) => line.split('|').map((part) => part.trim()))
      .filter((parts) => parts.length >= 4 && parts[1])
      .map(([days, label, cutoff, time]) => {
        const [hours, minutes] = time.split(':').map((n) => Number.parseInt(n, 10) || 0);
        return { days: days.toLowerCase(), label, cutoff: cutoff.toLowerCase(), hours, minutes };
      });
  }

  #appliesOn(rule, weekday) {
    const isWeekend = weekday === 0 || weekday === 6;
    if (rule.days.startsWith('weekday')) return !isWeekend;
    if (rule.days.startsWith('weekend')) return isWeekend;
    if (rule.days === 'daily' || rule.days === 'everyday') return true;
    return rule.days.split(/[\s,]+/).some((name) => name && DAY_NAMES.indexOf(name.slice(0, 3)) === weekday);
  }

  #cutoffEpoch(rule, year, month, day, weekday) {
    let daysBefore = Number.parseInt(rule.cutoff, 10);
    if (Number.isNaN(daysBefore)) {
      const target = DAY_NAMES.indexOf(rule.cutoff.slice(0, 3));
      if (target === -1) return Infinity;
      daysBefore = (weekday - target + 7) % 7 || 7;
    }
    return Date.UTC(year, month, day - daysBefore, rule.hours, rule.minutes) - SG_OFFSET_MS;
  }

  #slotKey(label) {
    return String(label || '').toLowerCase().replace(/[\s\-–—]/g, '');
  }

  #closed() {
    const days = [];
    const slots = {};
    for (const line of (this.dataset.closedDates || '').split(',')) {
      const [date, ...labels] = line.split('|');
      const key = date.trim().slice(0, 10);
      if (!key) continue;
      if (!labels.length) days.push(key);
      else slots[key] = (slots[key] || []).concat(labels.map((l) => this.#slotKey(l)));
    }
    return { days, slots };
  }

  #findEarliest(now) {
    const closed = this.#closed();
    for (let offset = 0; offset < 21; offset++) {
      const dateValue = new Date(now + SG_OFFSET_MS + offset * DAY_MS).toISOString().slice(0, 10);
      if (closed.days.includes(dateValue)) continue;
      const [year, month, day] = dateValue.split('-').map((n) => Number.parseInt(n, 10));
      const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
      const closedSlots = closed.slots[dateValue] || [];
      for (const rule of this.#rules) {
        if (!this.#appliesOn(rule, weekday)) continue;
        if (closedSlots.includes(this.#slotKey(rule.label))) continue;
        const cutoff = this.#cutoffEpoch(rule, year, month - 1, day, weekday);
        if (cutoff > now) return { offset, year, month, day, label: rule.label, cutoff };
      }
    }
    return null;
  }

  #render() {
    const now = Date.now();
    const slot = this.#findEarliest(now);
    const text = this.querySelector('[data-next-delivery-text]');
    const timeText = this.querySelector('[data-next-delivery-countdown]');
    if (!slot || !text) {
      this.hidden = true;
      return;
    }

    let dayText;
    if (slot.offset === 0) dayText = this.dataset.todayText || '';
    else if (slot.offset === 1) dayText = this.dataset.tomorrowText || '';
    else {
      dayText = new Intl.DateTimeFormat(document.documentElement.lang || 'en-SG', {
        weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
      }).format(new Date(Date.UTC(slot.year, slot.month - 1, slot.day)));
    }
    text.textContent = (this.dataset.template || '').replace('__DAY__', dayText).replace('__SLOT__', slot.label);

    if (timeText) {
      const mins = Math.max(1, Math.ceil((slot.cutoff - now) / 60000));
      if (mins <= 24 * 60) {
        const hours = Math.floor(mins / 60);
        const time = hours > 0 ? `${hours}h ${mins % 60}m` : `${mins}m`;
        timeText.textContent = (this.dataset.countdownTemplate || '').replace('__TIME__', time);
        timeText.hidden = false;
      } else {
        timeText.hidden = true;
      }
    }
    this.hidden = false;
  }
}

if (!customElements.get('s-next-delivery')) {
  customElements.define('s-next-delivery', SNextDelivery);
}
